import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';

import { SITE_URL, DEFAULT_OG_IMAGE } from './site';

// Matches index.html. Pages that do not set their own og:type fall back to it, so
// navigating away from one that did never leaves its metadata behind.
const DEFAULT_OG_TYPE = 'website';

// Marks the script elements holding structured data for the current page, added
// alongside the site-wide Organization and SoftwareApplication graph in index.html
// rather than replacing it.
const PAGE_JSON_LD_ATTRIBUTE = 'data-page-json-ld';

// Everything a route contributes to the document head, resolved from its route data.
interface PageMetadata {
  ttl: string;
  description: string;
  ogImage: string;
  ogType: string;
  // ISO dates for article:published_time and article:modified_time, or null on pages
  // that are not articles.
  articlePublishedTime: string | null;
  articleModifiedTime: string | null;
  // One entry per JSON-LD block: route data may give a single object or several (a blog
  // post has an Article and a BreadcrumbList, and a FAQPage if it has FAQs).
  jsonLd: object[];
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  titleSuffix = 'Dash - meeting room display & booking system for tablets, TVs & PoE panels';
  defaultDescription = 'Turn any tablet, phone, or smart TV into a meeting room display, or use a Dash-ready PoE panel with LED status lights.  One-tap room booking, check-in with no-show release, Google Workspace & Microsoft 365. Free read-only plan; booking from $3.99 per display per month.';
  navbarOpen = false;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document
  ) {
  }

  isGuide = () => {
    return this.router.url?.startsWith('/enterprise');
  }

  toggleNavbar() {
      this.navbarOpen = !this.navbarOpen;
  }

  closeNavbar() {
        this.navbarOpen = false;
  }

  ngOnInit() {
      const appTitle = this.titleService.getTitle();
      this.router
        .events.pipe(
          filter(event => event instanceof NavigationEnd),
          map(() => {
            const child = this.activatedRoute.firstChild;
            // `fullTitle` is used verbatim; `title` is a prefix the site suffix is appended to.
            const ttl = child.snapshot.data.fullTitle
              || (child.snapshot.data.title ? child.snapshot.data.title + this.titleSuffix : appTitle);
            const description = child.snapshot.data.description || this.defaultDescription;
            // Set by article-style pages (the blog); every other page keeps the
            // site-wide defaults from index.html.
            const ogImage = child.snapshot.data.ogImage || DEFAULT_OG_IMAGE;
            const ogType = child.snapshot.data.ogType || DEFAULT_OG_TYPE;
            const articlePublishedTime = child.snapshot.data.articlePublishedTime || null;
            const articleModifiedTime = child.snapshot.data.articleModifiedTime || null;
            const routeJsonLd = child.snapshot.data.jsonLd;
            const jsonLd = Array.isArray(routeJsonLd) ? routeJsonLd : (routeJsonLd ? [routeJsonLd] : []);
            return { ttl, description, ogImage, ogType, articlePublishedTime, articleModifiedTime, jsonLd };
          })
        ).subscribe(({
          ttl, description, ogImage, ogType, articlePublishedTime, articleModifiedTime, jsonLd
        }: PageMetadata) => {
          this.titleService.setTitle(ttl);
          this.metaService.updateTag({ property: 'og:title', content: ttl });
          this.metaService.updateTag({ name: 'description', content: description });
          this.metaService.updateTag({ property: 'og:description', content: description });
          this.metaService.updateTag({ property: 'og:image', content: ogImage });
          this.metaService.updateTag({ name: 'twitter:image', content: ogImage });
          this.metaService.updateTag({ property: 'og:type', content: ogType });
          this.updateArticleTag('article:published_time', articlePublishedTime);
          this.updateArticleTag('article:modified_time', articleModifiedTime);
          this.updatePageJsonLd(jsonLd);
        });

      this.router
        .events.pipe(
          filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
          this.updateCanonicalUrl(event.urlAfterRedirects);
        });
      this.updateCanonicalUrl(this.router.url);
  }

  // Article tags are not in index.html: they only exist on article pages, so navigating
  // away from one removes them rather than leaving the article's dates on the next page.
  private updateArticleTag(property: string, content: string | null) {
    if (content) {
      this.metaService.updateTag({ property, content });
    } else {
      this.metaService.removeTag(`property='${property}'`);
    }
  }

  // og:url always matches the canonical URL. Like the canonical link, it is added here
  // rather than given a default in index.html, where it would name the home page on
  // every page that was not prerendered.
  private updateCanonicalUrl(url: string) {
    const path = url.split('?')[0].split('#')[0];
    const canonicalUrl = SITE_URL + (path === '/' ? '' : path);

    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
  }

  // Written into one script element per block, reused across navigations; any left over
  // from a page that supplied more blocks (or a page with none at all) are removed.
  private updatePageJsonLd(jsonLd: object[]) {
    const scripts = Array.from(
      this.document.querySelectorAll<HTMLScriptElement>(`script[${PAGE_JSON_LD_ATTRIBUTE}]`));

    scripts.slice(jsonLd.length).forEach(script => script.remove());

    jsonLd.forEach((block, index) => {
      let script = scripts[index];
      if (!script) {
        script = this.document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute(PAGE_JSON_LD_ATTRIBUTE, '');
        this.document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(block);
    });
  }

}
